import React, { useState } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";
import type Stripe from "stripe";

import { Button } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import { Input } from "js/Components/Input";
import { getCoupon } from "common/utils/stripeUtils";
import CheckoutController, { CheckoutControllerState } from "js/controllers/CheckoutController";
import { t } from "js/i18n";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { themeColors } from "js/react/sharedStyles";
import { formatPrice } from "js/react/views/Billing/utils/prices";

const PromoContainer = styled.div`
    width: 100%;
    min-height: 44px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;

    .bai-button {
        height: 30px;
        padding: 5px;
        color: ${themeColors.ui_blue};
        text-align: right;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        letter-spacing: 1px;
        text-transform: uppercase;

        &:hover {
            &:before {
                opacity: 0.2;
                background: transparent;
            }
        }
    }
`;

const PromoInputContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 20px;

    > :first-child {
        flex-grow: 4;
    }
    > :not(:first-child) {
        flex-grow: 1;
    }
`;

const PromoInput = styled(Input)`
    width: 100%;
    display: flex;
    padding: 8px 10px;
    align-items: center;
    gap: 5px;
    align-self: stretch;

    border-radius: 1px;
    border: 1px solid rgba(238, 238, 238, 0.85);
    background: #FFF;

    > input {
        color: #888;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 100%;

        &::placeholder {
            color: #888;
            font-style: normal;
        }
    }
`;

const PromoTerms = styled.div`
    color: #888;
    font-size: 9px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    text-align: center;

    > a {
        text-decoration: none;
        color: ${themeColors.ui_blue};
    }
`;

const PromoError = styled.div`
    font-size: 9px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    color: ${themeColors.warning};
`;

const StyledPromoCoupon = styled.div`
    display: flex;
    flex-flow: row;
    gap: 10px;
    font-size: 15px;
    font-weight: 900;
    color: #333;

    .bai-icon {
        font-size: 22.5px;
        margin-top: -1px;
    }
`;

const DueDateText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.25;
    color: #888888;
`;

const DueTodayText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
    letter-spacing: 0.5px;
    color: #444444;
`;

export const PromoForm = CheckoutController.withState(function PromoForm({ promo }: CheckoutControllerState) {
    const [isAddingPromo, setIsAddingPromo] = useState(false);
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [promoCode, setPromoCode] = useState("");

    const handleAddPromo = () => {
        setIsAddingPromo(true);
    };

    const handleChangePromoCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newCode = e.target.value;

        setPromoCode(newCode);
        setPromoError(null);

        if (promoCode !== "" && newCode === "") {
            setIsAddingPromo(false);
        }
    };

    const handleApplyPromo = async () => {
        setIsApplyingPromo(true);
        setPromoError(null);

        try {
            const error = await CheckoutController.applyPromoCode(promoCode);
            if (error) {
                setPromoError(t(error));
            }
        } catch (err) {
            setPromoError(err instanceof Error ? err.message : t("Something went wrong"));
        } finally {
            setIsApplyingPromo(false);
        }
    };

    const promoCoupon = getCoupon(promo);
    if (promoCoupon) {
        return <PromoCoupon coupon={promoCoupon} />;
    }

    if (!isAddingPromo) {
        return (
            <PromoContainer data-test-id="promo-container">
                <Button unframed large blue onClick={handleAddPromo}>{t("add promo code")}</Button>
            </PromoContainer>
        );
    }

    return (
        <PromoContainer data-test-id="promo-container">
            <FetchingClickShield visible={isApplyingPromo} backgroundColor="rgba(255,255,255,0.7)" />

            <PromoInputContainer>
                <PromoInput width="100%" value={promoCode} onChange={handleChangePromoCode} placeholder={t("Promo Code")} />
                <Button unframed large blue onClick={handleApplyPromo}
                    disabled={!promoCode || promoError || isApplyingPromo}>{t("apply")}</Button>
            </PromoInputContainer>
            {!promoError && <PromoTerms>
                <Trans
                    i18nKey="By applying, you agree to the <promoTermsLink>Promotion Terms and Conditions</promoTermsLink>"
                    components={{ promoTermsLink: <a href="/promotion-terms" target="_blank" /> }}
                />
            </PromoTerms>}
            {promoError && <PromoError>{promoError}</PromoError>}
        </PromoContainer>
    );
});

export function PromoCoupon({ coupon }: { coupon: Stripe.Coupon }) {
    const durationText = coupon.duration === "repeating"
        ? t("for {{months}} months", { months: coupon.duration_in_months })
        : t(coupon.duration === "once" ? "once" : "forever");
    const currency = CheckoutController.currency;
    const conversionRate = CheckoutController.conversionRate;
    const amountText = coupon.amount_off ? formatPrice(coupon.amount_off / 100 * conversionRate, currency) : `${coupon.percent_off}%`;

    return (
        <PromoContainer data-test-id="promo-container">
            <StyledPromoCoupon>
                <Icon color="rgb(143, 177, 48)">confirmation_number</Icon>
                {
                    coupon.duration === "once"
                        ? (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <DueTodayText>{t("{{percent}}% off", { percent: coupon.percent_off })}</DueTodayText>
                                <DueDateText>{t("First billing cycle only")}</DueDateText>
                            </div>
                        )
                        : `${coupon.name} (${t("{{amount}} off {{duration}}", { amount: amountText, duration: durationText })})`
                }
            </StyledPromoCoupon>
        </PromoContainer>
    );
}
